import { useState, useEffect, type FormEvent } from 'react';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox } from '../components/admin/ui';
import { Tooltip } from '../components/Tooltip';
import { TOOLTIPS, type TooltipKey } from '../lib/tooltips';

/** Tela /ajustes (cliente, Fase 4 C2): playbook, e-mail remetente, membros. */

const PLAYBOOKS = [
  { chave: 'conservador', rotulo: 'Conservador', desc: 'Só informa a situação e convida ao contato.', tip: 'playbookConservador' },
  { chave: 'moderado', rotulo: 'Moderado', desc: 'Oferece diluir o reajuste em até 6 meses.', tip: 'playbookModerado' },
  { chave: 'agressivo', rotulo: 'Agressivo', desc: 'Diluição do reajuste + renegociação de atraso em até 3 parcelas.', tip: 'playbookAgressivo' },
] as const satisfies { chave: string; rotulo: string; desc: string; tip: TooltipKey }[];
type PlaybookChave = (typeof PLAYBOOKS)[number]['chave'];

export function AjustesPage() {
  const { data, loading, error, reload } = useAsync(() => trpc.ajustes.get.query());
  const { data: perfil } = useAsync(() => trpc.perfil.meu.query());
  const [playbook, setPlaybook] = useState<PlaybookChave>('conservador');
  const [remetente, setRemetente] = useState('');
  const [saving, setSaving] = useState(false);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (data) {
      setPlaybook(data.playbook);
      setRemetente(data.emailRemetente ?? '');
    }
  }, [data]);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSalvo(false);
    try {
      await trpc.ajustes.salvar.mutate({ playbook, emailRemetente: remetente });
      setSalvo(true);
      reload();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl" data-testid="ajustes-page">
      <h1 className="text-2xl font-semibold text-ink">Ajustes</h1>
      <p className="mt-1 text-sm text-ink-3">Como a plataforma se comunica em nome da sua incorporadora.</p>

      <div className="mt-4">
        {loading && <Loading />}
        {error && <ErrorBox msg={error} onRetry={reload} />}
        {data && (
          <form onSubmit={salvar} className="space-y-4">
            <div className="card p-5">
              <div className="text-[15px] font-semibold text-ink">Playbook de comunicação</div>
              <div className="mt-0.5 text-xs text-ink-3">Define o tom e as ofertas permitidas nos e-mails.</div>
              <div className="mt-3 space-y-2">
                {PLAYBOOKS.map((p) => (
                  <label
                    key={p.chave}
                    className={`flex cursor-pointer items-start gap-3 rounded-[12px] border p-3 ${
                      playbook === p.chave ? 'border-accent bg-accent-soft' : 'border-line'
                    }`}
                  >
                    <input
                      type="radio"
                      name="playbook"
                      checked={playbook === p.chave}
                      onChange={() => setPlaybook(p.chave)}
                      className="mt-1"
                    />
                    <div>
                      <div className="flex items-center gap-1.5 text-sm font-medium text-ink">
                        {p.rotulo}
                        <Tooltip text={TOOLTIPS[p.tip]} label={`O que é o playbook ${p.rotulo}`} />
                      </div>
                      <div className="text-xs text-ink-3">{p.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="card p-5">
              <div className="flex items-center gap-1.5 text-[15px] font-semibold text-ink">
                E-mail remetente
                <Tooltip text={TOOLTIPS.ajustesRemetente} label="O que é o e-mail remetente" />
              </div>
              <div className="mt-0.5 text-xs text-ink-3">Usado como remetente dos e-mails aprovados. Vazio = padrão da plataforma.</div>
              <input
                type="text"
                value={remetente}
                onChange={(e) => setRemetente(e.target.value)}
                placeholder="Incorporadora <contato@suaempresa.com.br>"
                className="mt-3 h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-ink md:h-10"
                data-testid="ajustes-remetente"
              />
            </div>

            <div className="card p-5">
              <div className="text-[15px] font-semibold text-ink">Seus dados de membro</div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-ink-3">WhatsApp</span>
                <span className="num font-medium text-ink" data-testid="ajustes-whatsapp">
                  {perfil?.whatsapp ?? '-'}
                </span>
              </div>
              <div className="mt-3 border-t border-line pt-3 text-sm text-ink-3">
                Cada conta é a sua incorporadora, criada automaticamente. O convite de sócios para a
                mesma incorporadora chega em breve.
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={saving}
                className="h-11 rounded-[10px] px-5 text-sm font-semibold text-white disabled:opacity-60 sm:h-10"
                style={{ background: 'var(--accent)' }}
                data-testid="ajustes-salvar"
              >
                {saving ? 'Salvando…' : 'Salvar ajustes'}
              </button>
              {salvo && <span className="text-sm text-accent">Salvo ✓</span>}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
